import React, { useContext, useId, useLayoutEffect } from 'react';

import type { WorkspaceReadinessRegistry } from './workspaceReadinessRegistry';

/**
 * Registry shared by the planning workspace gate and the components that load
 * inside it. `null` outside the planning route so shared components (overview
 * panel, sidebar, artifact panel) stay usable on public/shared pages.
 */
export const WorkspaceReadinessRegistryContext = React.createContext<WorkspaceReadinessRegistry | null>(null);

/**
 * True while the loading shell still covers the workspace (gated or fading).
 * Lets scroll/animation logic behave as "nobody is watching yet" — e.g. the
 * overview timeline follows hydration growth to the bottom instead of
 * preserving a reading anchor. `false` outside the planning gate.
 */
export const WorkspaceGateActiveContext = React.createContext<boolean>(false);

export const useWorkspaceGateActive = (): boolean => useContext(WorkspaceGateActiveContext);

const useWorkspaceReadinessRegistry = (): WorkspaceReadinessRegistry | null => (
  useContext(WorkspaceReadinessRegistryContext)
);

/**
 * Declare that this component is still loading while `pending` is true. The
 * hold is registered in a layout effect so the gate can never release in the
 * paint between a child mounting and its effects running, and it is owned by
 * this component instance so repeated effect runs cannot double-count.
 * No-op outside the planning workspace gate.
 */
export const useWorkspaceLoadingSignal = (key: string, pending: boolean): void => {
  const registry = useWorkspaceReadinessRegistry();
  const owner = useId();

  useLayoutEffect(() => {
    if (!registry || !pending) return undefined;

    return registry.hold(key, owner);
  }, [registry, key, owner, pending]);
};

/**
 * Element form of the signal, for Suspense fallbacks and other places where a
 * hook cannot be added: it holds `id` for as long as it stays mounted.
 */
export const WorkspaceLoadingSignal: React.FC<{ id: string }> = ({ id }) => {
  useWorkspaceLoadingSignal(id, true);

  return null;
};
